import React, {useState, useCallback, ReactElement} from 'react';
import {Layout, Typography, theme, Col, Row, Grid, Breadcrumb, Spin} from 'antd';
import { DiazarPageHeader } from '../Components/PageHeader/DiazarPageHeader';
import { DiazarAlerts } from '../Components/DiazarAlerts';
import { Footer } from 'antd/es/layout/layout';
import {ListItemsInfoLinks} from '../Components/ListItemsInfoLinks';
import {DiazarSider} from '../Components/MainLayout/DiazarSider';
import {useLayoutDispatch, useLayoutState} from '../Context/LayoutProvider/LayoutProvider';
import {setContextIsSmallScreen} from '../Context/LayoutProvider/LayoutProviderActions';
import {BreadcrumbItemType} from 'antd/es/breadcrumb/Breadcrumb';
import {useNavigate} from 'react-router-dom';
import {useAuth0} from '@auth0/auth0-react';
import {useAuthDispatch, useAuthState} from '../Context/AuthProvider/AuthProvider';
import {useAlertDispatcherState} from '../Context/AlertProvider/AlertProvider';
import {logout} from '../Context/AuthProvider/AuthProviderActions';
import {addAlert} from '../Context/AlertProvider/AlertProviderActions';

const { Content, Header } = Layout;
const { Text, Title } = Typography;

const {useBreakpoint} = Grid;

export interface BreadcrumbViewProps {
    title: string;
    path?: string;
}

interface ViewProps {
    title: string | ReactElement;
    children?: React.ReactNode;
    description?: React.ReactNode;
    disableOtherItems?: boolean;
    breadcrumbItems?: BreadcrumbViewProps[];
    isLoading?: boolean;
    loadingTip?: string;
    isPublic?: boolean;
}

export const PageLayout: React.FunctionComponent<ViewProps> = (props) => {
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const isPrivate = props.isPublic === undefined || !props.isPublic;
    const useAuth0Data = useAuth0();

    const authDistpatch = useAuthDispatch();
    const alertDistpatch = useAlertDispatcherState();

    const sessionData = useAuthState();

    const [isBackToTopVisible, setIsBackToTopVisible] = useState<boolean>(false);

    const screens = useBreakpoint();

    const navigate = useNavigate();

    const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setIsBackToTopVisible(e.currentTarget.scrollTop > 100);
    }, []);

    const TITLE = React.useMemo(() => {
        if (typeof props.title === 'string') {
            return <Title level={2}>{props.title}</Title>;
        }
        return props.title;
    } , [props.title]);

    const BREADCRUMBS_ITEMS = React.useMemo( ()=>{
        if ( props.breadcrumbItems === undefined || props.breadcrumbItems.length === 0 ){
            return undefined;
        }

        const items : BreadcrumbItemType[] = [];
        const length = props.breadcrumbItems.length;
        props.breadcrumbItems.forEach( (item, index) => {
            const itemType = {
                title: item.title,
                key: index,
                onClick:  (e)=>{
                    e.preventDefault();
                    if (item.path !== undefined){
                        navigate(item.path);
                    }
                },
                href: (index === length - 1 ) ? undefined : item.path !== undefined ? item.path : '#',
            };
            items.push(itemType);
        });

        return items;
    }, [navigate, props.breadcrumbItems] );


    const {collapsed, isSmallScreen} = useLayoutState();
    const dispatch = useLayoutDispatch();

    React.useEffect(() => {
        const isSmall = !screens.lg;
        if(isSmallScreen === undefined || isSmall !== isSmallScreen){
            setContextIsSmallScreen(isSmall, dispatch);
        }
    }, [dispatch, collapsed, isSmallScreen, screens.lg]);

    const spinningLoading = React.useMemo( ()=>{
        if ( props.isLoading === undefined ){
            return false;
        }

        return props.isLoading;
    }, [props.isLoading] );


    React.useEffect( ()=> {
        const companyMigated = sessionData ? sessionData.company?.isMigrated : false;
        if ( !companyMigated || !isPrivate || useAuth0Data.isLoading) return;

        setTimeout(()=>{
            if( !(useAuth0Data.isAuthenticated && useAuth0Data.user !== undefined) ){
                logout(authDistpatch).then( _r => {
                    addAlert('Sesión Expirada', 'Tu sessión ha expirado, para continuar inicie sesión nuevamente', 'error', alertDistpatch);
                } );
            }
        }, 1000);
    }, [useAuth0Data.isAuthenticated, useAuth0Data.isLoading, isPrivate, navigate, useAuth0Data.user, authDistpatch, alertDistpatch, sessionData]);


    return (
        <Spin spinning={spinningLoading} tip={props.loadingTip}>
        <Layout style={{ minHeight: '100vh' }}>
            <DiazarSider disableOtherItems={ props.disableOtherItems } />

            <Layout
                style={
                    {
                        overflow: 'hidden',
                    }
                }
            >
                <Header style={{
                    width: '100%',
                    zIndex: 1000,
                    padding: 0,
                    background: colorBgContainer,
                    transition: 'width 0.2s',
                }}
                >
                    <DiazarPageHeader />
                </Header>

                <Content
                    style={{
                        overflow: 'auto', // Permite el scroll cuando el contenido es grande
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                        height: 'calc( 100vh - 264px)',
                    }}
                >
                    <DiazarAlerts />

                    <div
                        style={{
                            overflowY: 'auto',
                            height: '100%',
                            padding: '16px',
                        }}
                        onScroll={onScroll}
                        className="page-content"
                    >
                            <Row>
                                <Col style={{width:'100%'}}>
                                    <Breadcrumb
                                        items={BREADCRUMBS_ITEMS}
                                    />
                                    { TITLE }
                                    {props.description}
                                </Col>
                            </Row>
                        <div style={{width:'100%'}}>
                            <Row style={{paddingBottom: 75, width:'100%'}}>
                                <Col span={24} style={{width:'100%'}}>{props.children}</Col>
                            </Row>
                        </div>
                    </div>
                </Content>

                <Footer className="layout-footer" style={{padding:8}}>

                    <Row className="info-text" gutter={20} >
                        <Col lg={12} style={{textAlign:'center'}}>
                            <Text >Todos los derechos reservados Diazar.com 2024©</Text>
                        </Col>
                        <Col lg={12} style={{textAlign:'center'}}>
                            <Text >
                                <ListItemsInfoLinks />
                            </Text>
                        </Col>
                    </Row>

                </Footer>
            </Layout>


        </Layout>
        </Spin>
    );
};
